import React, { useEffect, useState } from "react";
import { startRegistration, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Fingerprint, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const PASSKEY_FLAG = "eim_passkey_device";

export default function PasskeySettings() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const supported = browserSupportsWebAuthn();

  const load = async () => {
    try { const { data } = await api.get("/auth/passkeys"); setRows(data); if (!data.length) localStorage.removeItem(PASSKEY_FLAG); } catch { setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const register = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/passkeys/register/options");
      const credential = await startRegistration({ optionsJSON: JSON.parse(data.options) });
      await api.post("/auth/passkeys/register/verify", { ceremony: data.ceremony, credential, name: name.trim() || null });
      localStorage.setItem(PASSKEY_FLAG, "1");
      setName("");
      toast.success("Face ID registrado en este dispositivo.");
      load();
    } catch (err) {
      toast.error(err.response ? formatApiError(err.response.data?.detail) : "Registro cancelado.");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Quitar este dispositivo? Ya no podrás ingresar con su Face ID.")) return;
    try { await api.delete(`/auth/passkeys/${id}`); toast.success("Dispositivo eliminado."); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <section className="bg-white rounded-2xl border border-border p-6 space-y-4" data-testid="passkey-settings">
      <div className="flex items-center gap-2"><Fingerprint className="w-5 h-5 text-accent" /><h2 className="text-lg font-semibold">Ingreso con Face ID</h2></div>
      <p className="text-sm text-muted-foreground">Registra tu iPhone o Mac para ingresar solo con Face ID / Touch ID, sin clave ni código por correo. Exclusivo para superadmin.</p>
      {!supported && <p className="text-sm text-amber-700" data-testid="passkey-unsupported">Este navegador no admite Face ID / llaves de acceso.</p>}
      <div className="flex flex-wrap gap-3">
        <Input className="h-11 rounded-xl max-w-xs" placeholder="Nombre (ej. iPhone de Alan)" value={name}
          onChange={(e) => setName(e.target.value)} data-testid="passkey-name-input" />
        <Button onClick={register} disabled={busy || !supported} variant="outline" className="rounded-xl border-2" data-testid="btn-register-passkey">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registrar este dispositivo"}
        </Button>
      </div>
      {rows.length > 0 && (
        <ul className="divide-y divide-border border border-border rounded-xl" data-testid="passkey-list">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between px-4 py-3 text-sm" data-testid={`passkey-row-${r.id}`}>
              <span><strong>{r.name}</strong> · {r.rp_id} · último uso: {r.last_used_at ? new Date(r.last_used_at).toLocaleString("es-CL") : "nunca"}</span>
              <Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid={`btn-delete-passkey-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
